/**
 * @module @yimoka/i18n/sync/storage
 * @summary I18nStorage 端口的 JSON 包装
 * @author keep
 * @since 2026-05-21
 */

import type { I18nCacheMeta, I18nStorage } from '../types';

/** 快照内容 */
export interface StoredSnapshot {
  data: Record<string, unknown>;
  meta?: I18nCacheMeta;
}

/** 包内消费的快照读写接口 */
export interface InternalSnapshotStorage {
  getSnapshot(lang: string): Promise<StoredSnapshot | null>;
  setSnapshot(lang: string, data: Record<string, unknown>, meta?: I18nCacheMeta): Promise<void>;
}

/**
 * 将业务端口 `I18nStorage`（最小 IO）包装为内部 `InternalSnapshotStorage`，
 * 统一负责 JSON 序列化与 `prefix` 拼接。
 *
 * @param adapter - 业务注入的 IO 端口
 * @returns 内部使用的快照读写器
 */
export function wrapStorage(adapter: I18nStorage): InternalSnapshotStorage {
  const prefix = adapter.prefix ?? '';
  const buildKey = (lang: string) => `${prefix}${lang}`;

  return {
    async getSnapshot(lang) {
      const raw = await adapter.get(buildKey(lang));
      if (!raw) {
        return null;
      }
      try {
        const parsed = JSON.parse(raw);
        if (
          parsed
          && typeof parsed === 'object'
          && !Array.isArray(parsed)
          && 'data' in parsed
          && parsed.data
          && typeof parsed.data === 'object'
        ) {
          return parsed as StoredSnapshot;
        }
      } catch {
        // ignore bad JSON
      }
      return null;
    },

    async setSnapshot(lang, data, meta) {
      const payload: StoredSnapshot = { data, meta };
      await adapter.set(buildKey(lang), JSON.stringify(payload));
    },
  };
}
